import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaFilter, FaEllipsisV, FaTimes, FaUndo } from "react-icons/fa";
import SearchInput from "./SearchInput";
import StatChips from "./StatChips";
import Modal from "./Modal";
import FilterTabs from "./FilterTabs";

/**
 * ListToolbar — liste sayfalarının ortak üst bar organizması.
 * Atomic design: organizma. ListView ile birlikte kullanılır.
 *
 * Bölümlerin tamamı opsiyoneldir — bazı listelerde başlık/aksiyon olmaz,
 * bazılarında filtre veya arama olmaz:
 *   - Satır 1: sol → ikon + başlık + alt başlık ; sağ → sayaç + filtre ikonu (mobil) + aksiyonlar
 *   - Satır 2: filtre butonları (genelde <FilterTabs/>) — sadece sm+
 *   - Satır 3: arama alanları (BOUNDED genişlik) — sadece sm+
 *
 * Mobil davranış:
 *   - Filtre + arama alanları filtre ikonuna taşınır; ikona basınca modal açılır.
 *     Modaldaki arama inputları taslak değerle çalışır — API istekleri ancak
 *     "Uygula"ya basınca tetiklenir.
 *   - `actionItems` verilirse aksiyonlar mobilde tek ⋮ menüsünde toplanır
 *     (masaüstünde etiketli butonlar). Boşsa ⋮ görünmez.
 *   - `mobileFilterSlotId` verilirse filtre ikonu o id'li elemana portal'lanır
 *     (ör. üstteki PageHeader satırında ⋮'nın soluna).
 *
 * @param {React.ComponentType} [icon]
 * @param {React.ReactNode} [title]
 * @param {React.ReactNode} [subtitle]
 * @param {number} [count]              - sayaç (null/undefined ise gizli)
 * @param {string} [countLabel]
 * @param {boolean} [isLoading]         - sayaç yerine spinner
 * @param {React.ReactNode} [actions]   - sağ üst aksiyon node'u (serbest)
 * @param {Array<{label,mobileLabel?,icon?,onClick,variant?,disabled?}>} [actionItems]
 *        - aksiyonların yapılandırılmış hali; mobil ⋮ menüsü bunu kullanır
 * @param {React.ReactNode} [filters]   - filtre satırı (ör. <FilterTabs/>)
 * @param {Array<{placeholder,value,onChange,type?,min?,hideOnMobile?,wrapperClassName?}>} [searchFields]
 * @param {React.ReactNode} [search]    - özel arama node'u (searchFields yerine)
 * @param {React.ReactNode} [searchNote]- arama altı not (ör. "Aranıyor...")
 * @param {string} [mobileFilterSlotId] - filtre ikonunun portal'lanacağı element id'si
 * @param {string} [className]          - dış sarmalayıcı padding'ini override eder
 * @param {Array<{label,value,icon?,color?}>} [stats]
 *        - istatistik rozetleri; filtre satırının SAĞINDA durur. Sekmeli
 *          sayfalarda (ör. Ödemeler) başlık üstte tek sefer olduğu için
 *          rozetlere ayrı bir satır ayırmak boşluk israfıydı.
 */
const DEFAULT_WRAP =
  "px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 border-b border-gray-100 z-10 shrink-0";

const ACTION_DESKTOP_CLASS = {
  primary:
    "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white",
  success:
    "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white",
  default: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
};

const ACTION_MENU_CLASS = {
  primary: "text-indigo-700 bg-indigo-50 hover:bg-indigo-100",
  success: "text-emerald-700 bg-emerald-50 hover:bg-emerald-100",
  default: "text-gray-700 bg-gray-50 hover:bg-gray-100",
};

export default function ListToolbar({
  icon: Icon,
  title,
  subtitle,
  count,
  countLabel,
  countIcon: CountIcon,
  isLoading = false,
  actions,
  actionItems,
  filters,
  filterTabs,
  searchFields,
  search,
  searchNote,
  stats,
  mobileFilterSlotId,
  // Filtreleri ilk haline döndüren opsiyonel "Temizle" aksiyonu
  onClear,
  className,
}) {
  const hasActionItems = Array.isArray(actionItems) && actionItems.length > 0;
  const hasStats = Array.isArray(stats) && stats.length > 0;
  const hasTopRow =
    title || subtitle || Icon || actions || hasActionItems || count != null;
  const hasSearch = (searchFields && searchFields.length > 0) || search;
  // filterTabs: yapılandırılmış FilterTabs config'i — modalda taslak modda
  // çalışır (Uygula'ya kadar API tetiklenmez). `filters` node'u eski kullanım
  // için desteklenmeye devam eder (modalda canlı çalışır).
  const hasFilterTabs =
    filterTabs && Array.isArray(filterTabs.filters) && filterTabs.filters.length > 0;
  const hasFilterContent = Boolean(filters) || hasFilterTabs || hasSearch;

  // Mobil filtre modalı
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  // Mobil ⋮ aksiyon menüsü
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  // Modal içindeki arama alanları ve filtre sekmeleri taslak değerle çalışır —
  // basınca/yazarken API'ye istek gitmez, "Uygula"ya basınca parent
  // onChange'leri çağrılır.
  const [draftValues, setDraftValues] = useState([]);
  const [draftFilter, setDraftFilter] = useState(null);
  // Filtre ikonu için portal hedefi (ör. PageHeader satırı)
  const [filterSlotEl, setFilterSlotEl] = useState(null);
  useEffect(() => {
    if (!mobileFilterSlotId) {
      setFilterSlotEl(null);
      return;
    }
    setFilterSlotEl(document.getElementById(mobileFilterSlotId) || null);
  }, [mobileFilterSlotId]);

  // Dolu arama alanı varsa filtre ikonunda nokta göster
  const hasActiveSearch = (searchFields || []).some((f) => f?.value);

  const openFilterModal = () => {
    setDraftValues((searchFields || []).map((f) => f?.value ?? ""));
    setDraftFilter(hasFilterTabs ? filterTabs.activeFilter : null);
    setFilterModalOpen(true);
  };

  const applyFilterModal = () => {
    if (hasFilterTabs && draftFilter != null && draftFilter !== filterTabs.activeFilter) {
      filterTabs.onChange?.(draftFilter);
    }
    (searchFields || []).forEach((f, i) => {
      if ((draftValues[i] ?? "") !== (f?.value ?? "")) {
        f.onChange?.({ target: { value: draftValues[i] ?? "" } });
      }
    });
    setFilterModalOpen(false);
  };

  // Masaüstü "Temizle" butonu — hep satırın en sağında, belirgin görünüm
  const clearButton = onClear ? (
    <button
      type="button"
      onClick={onClear}
      className="flex items-center gap-1.5 px-3.5 h-[38px] bg-white border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors text-xs font-semibold shrink-0"
      title="Filtreleri temizle"
    >
      <FaUndo className="w-3 h-3" />
      Temizle
    </button>
  ) : null;

  const filterButton = hasFilterContent ? (
    <button
      type="button"
      onClick={openFilterModal}
      className="relative sm:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-gray-300 text-gray-600 shadow-sm active:bg-gray-100 transition-colors shrink-0"
      title="Filtrele"
    >
      <FaFilter className="w-3.5 h-3.5" />
      {hasActiveSearch && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-indigo-500 border-2 border-white" />
      )}
    </button>
  ) : null;

  // Filtre ikonu: slot varsa oraya portal'lanır (⋮'nın soluna),
  // yoksa toolbar'ın kendi üst satırında gösterilir.
  const inlineFilterButton = filterSlotEl ? null : filterButton;

  // Mobilde uygulanan filtreler alt satırda chip olarak gösterilir.
  const defaultFilterId = hasFilterTabs ? filterTabs.filters[0]?.id : null;
  const activeFilterDef =
    hasFilterTabs && filterTabs.activeFilter !== defaultFilterId
      ? filterTabs.filters.find((f) => f.id === filterTabs.activeFilter)
      : null;
  const activeSearchChips = (searchFields || [])
    .map((f, i) => ({ ...f, index: i }))
    .filter((f) => f?.value);
  const hasMobileChips = Boolean(activeFilterDef) || activeSearchChips.length > 0;

  // Mobilde toolbar'ın gövdesi boşsa (üst satır yok, filtre ikonu portal'landı,
  // filtre/arama zaten modala taşındı, aktif chip yok) konteyner padding+border
  // ile boş bir şerit bırakmasın diye tamamen gizlenir. Modal ve portal body'ye
  // render edildiği için çalışmaya devam eder.
  const mobileInlineEmpty = !hasTopRow && !inlineFilterButton && !hasMobileChips;

  return (
    <div
      className={`${mobileInlineEmpty ? "hidden sm:block" : ""} ${
        className
          ? `${className} bg-gray-50/80 sm:bg-gray-50 border-b border-gray-100 z-10 shrink-0`
          : DEFAULT_WRAP
      }`}
    >
      {filterSlotEl && filterButton && createPortal(filterButton, filterSlotEl)}

      {hasTopRow ? (
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <div className="flex items-center gap-2 shrink-0 min-w-0">
            {Icon && (
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg sm:rounded-xl shadow-sm border border-indigo-100 shrink-0">
                <Icon className="text-indigo-600 w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            )}
            {(title || subtitle) && (
              <div className="min-w-0">
                {title && (
                  <h1 className="text-sm sm:text-lg font-bold text-gray-900 truncate">{title}</h1>
                )}
                {subtitle && (
                  <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">{subtitle}</p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2.5 justify-end shrink-0">
            {count != null && (
              <div className="flex items-center gap-1.5 sm:gap-2 bg-indigo-50/80 rounded-md sm:rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 border border-indigo-100/80">
                {CountIcon && (
                  <CountIcon className="text-indigo-500 w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                )}
                <span className="text-xs sm:text-base font-bold text-indigo-800">
                  {isLoading ? (
                    <span className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin inline-block" />
                  ) : (
                    count
                  )}
                </span>
                {countLabel && (
                  <span className="text-[10px] sm:text-xs text-indigo-500 font-medium hidden sm:inline">
                    {countLabel}
                  </span>
                )}
              </div>
            )}
            {inlineFilterButton}

            {/* Yapılandırılmış aksiyonlar: masaüstünde buton, mobilde ⋮ menüsü */}
            {hasActionItems && (
              <>
                {actionItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    disabled={item.disabled || isLoading}
                    title={item.label}
                    className={`hidden sm:flex items-center gap-2 px-3.5 py-2 font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                      ACTION_DESKTOP_CLASS[item.variant] || ACTION_DESKTOP_CLASS.default
                    }`}
                  >
                    {item.icon && <item.icon className="w-4 h-4" />}
                    <span>{item.label}</span>
                  </button>
                ))}
                <div className="relative sm:hidden shrink-0">
                  <button
                    type="button"
                    onClick={() => setActionsMenuOpen((v) => !v)}
                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-gray-300 text-gray-600 shadow-sm active:bg-gray-100 transition-colors"
                    title="İşlemler"
                  >
                    <FaEllipsisV className="w-4 h-4" />
                  </button>
                  {actionsMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setActionsMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 z-40 w-56 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden p-1.5 space-y-1">
                        {actionItems.map((item) => (
                          <button
                            key={item.label}
                            onClick={() => {
                              setActionsMenuOpen(false);
                              item.onClick?.();
                            }}
                            disabled={item.disabled || isLoading}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors disabled:opacity-50 ${
                              ACTION_MENU_CLASS[item.variant] || ACTION_MENU_CLASS.default
                            }`}
                          >
                            {item.icon && <item.icon className="w-4 h-4 shrink-0" />}
                            <span>{item.mobileLabel || item.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            {actions}
          </div>
        </div>
      ) : (
        // Başlık satırı olmayan listelerde (ör. firma listesi) mobil filtre
        // ikonu kendi satırında sağda durur (slot'a portal'lanmadıysa).
        inlineFilterButton && (
          <div className="flex justify-end mb-2 sm:hidden">{inlineFilterButton}</div>
        )
      )}

      {/* Mobil: uygulanan filtre/arama chip'leri — modaldan seçilenler burada görünür */}
      {hasMobileChips && (
        <div className="sm:hidden flex flex-wrap items-center gap-1.5 mb-1">
          {activeFilterDef && (
            <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-indigo-600 text-white text-xs font-semibold shadow-sm">
              {activeFilterDef.icon && <activeFilterDef.icon className="w-3 h-3 shrink-0" />}
              {activeFilterDef.label}
              <button
                type="button"
                onClick={() => filterTabs.onChange?.(defaultFilterId)}
                className="ml-0.5 w-4 h-4 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                title="Filtreyi kaldır"
              >
                <FaTimes className="w-2 h-2" />
              </button>
            </span>
          )}
          {activeSearchChips.map((f) => (
            <span
              key={f.placeholder || f.index}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium"
            >
              <span className="text-indigo-400">{f.placeholder}:</span>
              <span className="font-semibold">{f.value}</span>
              <button
                type="button"
                onClick={() => f.onChange?.({ target: { value: "" } })}
                className="ml-0.5 w-4 h-4 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center"
                title="Aramayı temizle"
              >
                <FaTimes className="w-2 h-2" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Filtre + arama satırları — mobilde gizli, modala taşınır.
          Arama satırı yoksa Temizle filtre satırının en sağında durur. */}
      {(filters || hasFilterTabs) && (
        <div className="hidden sm:flex items-start gap-2">
          <div className="flex-1 min-w-0">{filters || <FilterTabs {...filterTabs} />}</div>
          {hasStats && (
            <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
              <StatChips stats={stats} isLoading={isLoading} />
            </div>
          )}
          {!hasSearch && clearButton && <div className="ml-auto shrink-0">{clearButton}</div>}
        </div>
      )}

      {/* Filtre satırı yoksa rozetler kendi satırında kalır */}
      {hasStats && !filters && !hasFilterTabs && (
        <div className="hidden sm:flex items-center justify-end gap-1.5 sm:gap-2.5">
          <StatChips stats={stats} isLoading={isLoading} />
        </div>
      )}

      {hasSearch && (
        <div className="hidden sm:flex flex-wrap items-end gap-2">
          {search}
          {(searchFields || []).map((f, i) => (
            <SearchInput
              key={f.placeholder || i}
              placeholder={f.placeholder}
              value={f.value}
              onChange={f.onChange}
              type={f.type || "text"}
              min={f.min}
              wrapperClassName={f.wrapperClassName || "w-full sm:w-56"}
            />
          ))}
          {clearButton && <div className="ml-auto self-end shrink-0">{clearButton}</div>}
        </div>
      )}

      {searchNote}

      {/* Mobil filtre modalı — filtre seçenekleri + arama alanları */}
      <Modal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="sm"
        zIndex={9999}
      >
        <Modal.Header title="Filtrele" onClose={() => setFilterModalOpen(false)} />
        <Modal.Body className="space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Filtre sekmeleri modalda taslak modda — Uygula'ya kadar API yok */}
          {hasFilterTabs ? (
            <FilterTabs
              {...filterTabs}
              activeFilter={draftFilter ?? filterTabs.activeFilter}
              onChange={(id) => setDraftFilter(id)}
              isLoading={false}
            />
          ) : (
            filters
          )}
          {hasSearch && (
            <div className="space-y-2">
              {search}
              {(searchFields || []).map((f, i) => (
                <SearchInput
                  key={f.placeholder || i}
                  placeholder={f.placeholder}
                  value={draftValues[i] ?? ""}
                  onChange={(e) =>
                    setDraftValues((prev) => {
                      const next = [...prev];
                      next[i] = e.target.value;
                      return next;
                    })
                  }
                  type={f.type || "text"}
                  min={f.min}
                  wrapperClassName="w-full"
                />
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {onClear && (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setFilterModalOpen(false);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-semibold hover:bg-gray-200 active:bg-gray-200 transition-colors"
              >
                <FaUndo className="w-3 h-3" />
                Temizle
              </button>
            )}
            <button
              type="button"
              onClick={applyFilterModal}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
            >
              Uygula
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
